import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

interface FormState {
  senderName: string;
  senderPhone: string;
  senderAddress: string;
  receiverName: string;
  receiverPhone: string;
  receiverAddress: string;
  parcelType: string;
  weight: string;
  pickupDate: string;
  pickupSlot: string;
  remark: string;
}

const initialState: FormState = {
  senderName: "",
  senderPhone: "",
  senderAddress: "",
  receiverName: "",
  receiverPhone: "",
  receiverAddress: "",
  parcelType: "",
  weight: "1kg 以内",
  pickupDate: "",
  pickupSlot: "尽快上门（30 分钟内）",
  remark: "",
};

const parcelTypes = ["文件证件", "食品生鲜", "药品", "数码产品", "服饰鞋包", "其他物品"];
const weights = ["1kg 以内", "1 - 5kg", "5 - 10kg", "10kg 以上"];
const slots = ["尽快上门（30 分钟内）", "上午 09:00 - 12:00", "下午 12:00 - 15:00", "下午 15:00 - 18:00", "晚上 18:00 - 21:00"];

export default function CourierSend() {
  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [showSuccess, setShowSuccess] = useState(false);

  usePageMeta(
    "快递寄件 - 美味的快递 | 在线下单同城急送",
    "在美味的快递在线填写寄件信息，选择包裹类型、重量与上门时间，最快 30 分钟取件，同城急送高效可靠。",
    "美味的快递,快递寄件,同城寄件,在线寄件,同城急送,快递下单"
  );

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.senderName.trim()) next.senderName = "请填写寄件人姓名";
    if (!/^1[3-9]\d{9}$/.test(form.senderPhone.trim())) next.senderPhone = "请填写正确的 11 位手机号";
    if (!form.senderAddress.trim()) next.senderAddress = "请填写取件地址";
    if (!form.receiverName.trim()) next.receiverName = "请填写收件人姓名";
    if (!/^1[3-9]\d{9}$/.test(form.receiverPhone.trim())) next.receiverPhone = "请填写正确的 11 位手机号";
    if (!form.receiverAddress.trim()) next.receiverAddress = "请填写收件地址";
    if (!form.parcelType) next.parcelType = "请选择包裹类型";
    if (!form.pickupDate) next.pickupDate = "请选择取件日期";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setShowSuccess(true);
    }
  };

  const inputClass = (hasError?: string) =>
    `w-full rounded-md border bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-400 ${
      hasError ? "border-primary-500" : "border-background-300 focus:border-primary-400"
    }`;

  return (
    <div className="bg-background-50">
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "快递服务", to: "/courier" }, { label: "快递寄件" }]} />
          <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">快递寄件</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            填写寄件与收件信息，选择包裹类型和上门时间，下单后骑手就近接单，最快 30 分钟上门取件。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <form onSubmit={onSubmit} noValidate className="flex flex-col gap-8 lg:col-span-2">
            <fieldset className="rounded-lg border border-background-200 bg-background-50 p-6">
              <legend className="flex items-center gap-2 px-1 font-heading text-base font-extrabold text-foreground-950">
                <i className="ri-user-location-line text-primary-600" />
                寄件人信息
              </legend>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground-700">寄件人姓名 *</label>
                  <input
                    type="text"
                    value={form.senderName}
                    onChange={(e) => update("senderName", e.target.value)}
                    placeholder="请输入寄件人姓名"
                    className={inputClass(errors.senderName)}
                  />
                  {errors.senderName && <p className="mt-1 text-xs text-primary-600">{errors.senderName}</p>}
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground-700">寄件人手机号 *</label>
                  <input
                    type="tel"
                    value={form.senderPhone}
                    onChange={(e) => update("senderPhone", e.target.value)}
                    placeholder="请输入 11 位手机号"
                    className={inputClass(errors.senderPhone)}
                  />
                  {errors.senderPhone && <p className="mt-1 text-xs text-primary-600">{errors.senderPhone}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-sm font-medium text-foreground-700">取件地址 *</label>
                  <input
                    type="text"
                    value={form.senderAddress}
                    onChange={(e) => update("senderAddress", e.target.value)}
                    placeholder="如：朝阳区望京 SOHO 塔 C 座 18 层"
                    className={inputClass(errors.senderAddress)}
                  />
                  {errors.senderAddress && <p className="mt-1 text-xs text-primary-600">{errors.senderAddress}</p>}
                </div>
              </div>
            </fieldset>

            <fieldset className="rounded-lg border border-background-200 bg-background-50 p-6">
              <legend className="flex items-center gap-2 px-1 font-heading text-base font-extrabold text-foreground-950">
                <i className="ri-map-pin-user-line text-accent-600" />
                收件人信息
              </legend>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground-700">收件人姓名 *</label>
                  <input
                    type="text"
                    value={form.receiverName}
                    onChange={(e) => update("receiverName", e.target.value)}
                    placeholder="请输入收件人姓名"
                    className={inputClass(errors.receiverName)}
                  />
                  {errors.receiverName && <p className="mt-1 text-xs text-primary-600">{errors.receiverName}</p>}
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground-700">收件人手机号 *</label>
                  <input
                    type="tel"
                    value={form.receiverPhone}
                    onChange={(e) => update("receiverPhone", e.target.value)}
                    placeholder="请输入 11 位手机号"
                    className={inputClass(errors.receiverPhone)}
                  />
                  {errors.receiverPhone && <p className="mt-1 text-xs text-primary-600">{errors.receiverPhone}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-sm font-medium text-foreground-700">收件地址 *</label>
                  <input
                    type="text"
                    value={form.receiverAddress}
                    onChange={(e) => update("receiverAddress", e.target.value)}
                    placeholder="如：海淀区中关村大街 1 号院 3 单元 502"
                    className={inputClass(errors.receiverAddress)}
                  />
                  {errors.receiverAddress && <p className="mt-1 text-xs text-primary-600">{errors.receiverAddress}</p>}
                </div>
              </div>
            </fieldset>

            <fieldset className="rounded-lg border border-background-200 bg-background-50 p-6">
              <legend className="flex items-center gap-2 px-1 font-heading text-base font-extrabold text-foreground-950">
                <i className="ri-box-3-line text-primary-600" />
                包裹与取件
              </legend>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground-700">包裹类型 *</label>
                  <select
                    value={form.parcelType}
                    onChange={(e) => update("parcelType", e.target.value)}
                    className={inputClass(errors.parcelType)}
                  >
                    <option value="">请选择包裹类型</option>
                    {parcelTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  {errors.parcelType && <p className="mt-1 text-xs text-primary-600">{errors.parcelType}</p>}
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground-700">包裹重量</label>
                  <select value={form.weight} onChange={(e) => update("weight", e.target.value)} className={inputClass()}>
                    {weights.map((w) => (
                      <option key={w} value={w}>
                        {w}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground-700">取件日期 *</label>
                  <input
                    type="date"
                    value={form.pickupDate}
                    onChange={(e) => update("pickupDate", e.target.value)}
                    className={inputClass(errors.pickupDate)}
                  />
                  {errors.pickupDate && <p className="mt-1 text-xs text-primary-600">{errors.pickupDate}</p>}
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground-700">取件时间段</label>
                  <select value={form.pickupSlot} onChange={(e) => update("pickupSlot", e.target.value)} className={inputClass()}>
                    {slots.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-sm font-medium text-foreground-700">
                    包裹内容说明 <span className="text-xs text-foreground-400">（选填，最多 500 字）</span>
                  </label>
                  <textarea
                    value={form.remark}
                    maxLength={500}
                    onChange={(e) => update("remark", e.target.value)}
                    rows={4}
                    placeholder="如：易碎品请轻拿轻放，需要保温箱配送等"
                    className={`${inputClass()} resize-none`}
                  />
                  <p className="mt-1 text-right text-xs text-foreground-400">{form.remark.length}/500</p>
                </div>
              </div>
            </fieldset>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="submit"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-8 py-3.5 text-sm font-bold text-background-50 transition hover:bg-accent-600"
              >
                <i className="ri-send-plane-fill" />
                提交寄件信息
              </button>
              <button
                type="reset"
                onClick={() => {
                  setForm(initialState);
                  setErrors({});
                }}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-8 py-3.5 text-sm font-bold text-foreground-700 transition hover:bg-background-100"
              >
                清空重填
              </button>
              <p className="text-xs text-foreground-400 sm:ml-2">* 提交信息仅用于前端演示，不会真实派单</p>
            </div>
          </form>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
                <i className="ri-information-line text-primary-600" />
                寄件须知
              </h2>
              <ul className="mt-4 flex flex-col gap-3 text-sm text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-accent-600" />
                  生鲜、药品类订单默认使用冷链保温箱配送。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-accent-600" />
                  贵重物品可在备注中说明，申请升级保价，最高可保 5 万元。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-accent-600" />
                  高峰期建议提前 2 小时下单，方便就近匹配骑手。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-accent-600" />
                  禁寄危险品、违禁品，一经发现将取消订单。
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
                <i className="ri-route-line text-accent-600" />
                参考费用
              </h2>
              <ul className="mt-4 flex flex-col gap-3 text-sm">
                <li className="flex items-center justify-between text-foreground-600">
                  <span>同城 3 公里内</span>
                  <span className="font-bold text-primary-600">¥9 起</span>
                </li>
                <li className="flex items-center justify-between text-foreground-600">
                  <span>同城 3 - 8 公里</span>
                  <span className="font-bold text-primary-600">¥15 起</span>
                </li>
                <li className="flex items-center justify-between text-foreground-600">
                  <span>8 公里以上</span>
                  <span className="font-bold text-primary-600">¥22 起</span>
                </li>
              </ul>
              <Link
                to="/courier-track"
                className="mt-5 flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-primary-300 bg-primary-50 px-5 py-2.5 text-sm font-bold text-primary-700 transition hover:bg-primary-100"
              >
                <i className="ri-search-eye-line" />
                已下单？去查询轨迹
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {showSuccess && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md animate-fade-up rounded-lg bg-background-50 p-8 text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-600">
              <i className="ri-checkbox-circle-fill text-4xl" />
            </span>
            <h2 className="mt-5 font-heading text-xl font-extrabold text-foreground-950">提交成功</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              我们已收到你的寄件信息，配送专员将在 5 分钟内与你电话确认取件时间。
              <br />
              <span className="text-xs text-foreground-400">（本次提交为前端演示，不会真实派单）</span>
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => {
                  setShowSuccess(false);
                  setForm(initialState);
                }}
                className="flex-1 rounded-full border border-background-300 bg-background-50 px-5 py-3 text-sm font-bold text-foreground-700 transition hover:bg-background-100"
              >
                继续寄件
              </button>
              <Link
                to="/courier-track"
                className="flex-1 rounded-full bg-primary-500 px-5 py-3 text-center text-sm font-bold text-background-50 transition hover:bg-primary-600"
              >
                去查询轨迹
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}